import { SelectableContainer } from '@acies/core';
import theme from './theme';

/**
 * The HTMLSelectListItemRenderer enables selection logic, when added as a child in html inside the HTMLSelectList component.
 *
 * Since we extends from SelectableContainer, selected property and event dispatching is handled by the super class, so this is basically for implementing design system styles.
 *
 * @example
 * 	<ac-html-select-list>
 * 	<ac-html-select-list-item-renderer [payload]="payload value here"></ac-html-select-list-item-renderer>
 * 	</<ac-html-select-list>
 *
 * 	When setting the [payload] property on the renderer,
 * 	that value will be set as the selectedItem property
 * 	of the parent HTMLSelectListEvent being dispatched.
 */
export default class HTMLSelectListItemRenderer extends SelectableContainer<unknown> {
	/** Whether the keyboard is on the renderer. Asked of the styled state, like every other look, so hovering has no say in it. */
	get #isFocused(): boolean {
		return this.styledState === 'focused' || this.styledState === 'focusedPressed';
	}

	public constructor() {
		super();
	}

	protected override initialize(): void {
		super.initialize();
		this.updateStyles();
	}

	protected override selectedChanged(): void {
		super.selectedChanged();
		this.updateStyles();
	}

	protected override stateChanged(): void {
		super.stateChanged();
		this.updateStyles();
	}

	/** The update * styles are all protected, so we can override them and apply other styles if needed. */
	protected updateFocusedStyles(): void {
		this.stroke = theme.strokes.focus_inside;
		this.fill = theme.colors.fill.focus;
	}

	protected updateIdleStyles(): void {
		this.stroke = null;
		this.fill = theme.colors.fill.white;
	}

	protected updateSelectedStyles(): void {
		this.stroke = null;
		this.fill = theme.colors.fill.selected;
	}

	protected updateStyles(): void {
		if (this.#isFocused) {
			this.updateFocusedStyles();
		} else if (this.selected) {
			this.updateSelectedStyles();
		} else {
			this.updateIdleStyles();
		}
	}
}
customElements.define('ac-html-select-list-item-renderer', HTMLSelectListItemRenderer);
